// CatalogRail — left-side filter rail for the /models catalog page.
//
// Two zones:
//   1. Pinned scope rows (All / Frequent / Favorites). Single-select —
//      picking one replaces the active scope and clears tag facets.
//      Mirrors the model selector detailed desktop so users who move
//      between picker and catalog read the same UI both places.
//   2. Tag facets — multi-select checkboxes. AND together on top of
//      the active scope. The header shows a "Clear" link when at
//      least one facet is checked.
//
// On XS we don't render this — the parent page uses a horizontal
// scroll-pills row instead. Encapsulating the rail keeps the page
// component focused on data wiring rather than this branching layout.

import React, { useCallback, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

export const RAIL_WIDTH = 220;

// Single-select scope row.
const PinnedRow = React.memo(function PinnedRow({ item, isActive, count, onPress }) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const handlePress = useCallback(() => {
    onPress(item.id);
  }, [onPress, item.id]);
  return (
    <Pressable
      onPress={handlePress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.pinned,
        {
          backgroundColor: isActive
            ? `${item.color}1F`
            : (hovered ? colors.solid_color_tertiary : 'transparent'),
        },
        Platform.OS === 'web' && { cursor: 'pointer' },
      ]}
    >
      <Icon name={item.icon} size={11} color={isActive ? item.color : colors.text_color_secondary} />
      <Text
        size="s"
        weight={isActive ? 'bold' : 'semibold'}
        numberOfLines={1}
        style={{ flex: 1, color: isActive ? item.color : colors.text_color_primary }}
      >
        {item.label}
      </Text>
      {count > 0 && (
        <Text
          size="s"
          weight="semibold"
          style={{
            color: isActive ? item.color : colors.text_color_tertiary,
            fontVariant: ['tabular-nums'],
          }}
        >
          {count > 999 ? '999+' : count}
        </Text>
      )}
    </Pressable>
  );
});

// Multi-select facet row.
const FacetRow = React.memo(function FacetRow({
  label, count, checked, color, facetId, onFacetToggle,
}) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const handlePress = useCallback(() => {
    onFacetToggle(facetId);
  }, [onFacetToggle, facetId]);
  return (
    <Pressable
      onPress={handlePress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.facet,
        {
          backgroundColor: checked
            ? `${color}1F`
            : (hovered ? colors.solid_color_tertiary : 'transparent'),
        },
        Platform.OS === 'web' && { cursor: 'pointer' },
      ]}
    >
      <View
        style={[
          styles.checkbox,
          {
            borderColor: checked ? color : colors.border_color_secondary,
            backgroundColor: checked ? color : 'transparent',
          },
        ]}
      >
        {checked && <Icon name="check" size={8} color="#fff" />}
      </View>
      <Text size="s" numberOfLines={1} style={{ flex: 1, color: checked ? color : colors.text_color_primary }} weight={checked ? 'bold' : 'normal'}>
        {label}
      </Text>
      <Text
        size="s"
        weight="semibold"
        style={{
          color: checked ? color : colors.text_color_tertiary,
          fontVariant: ['tabular-nums'],
        }}
      >
        {count}
      </Text>
    </Pressable>
  );
});

const CatalogRail = ({
  pinnedItems,
  pinnedCounts,
  activePinned,
  onPinnedChange,
  facets,
  selectedFacets,
  onFacetToggle,
  onFacetsClear,
  categoryLabel,
  clearLabel,
  emptyLabel,
}) => {
  const { colors } = useTheme();
  return (
    <View style={[styles.rail, { borderRightColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
      <View style={{ paddingTop: 8, paddingBottom: 16 }}>
        {pinnedItems.map(item => (
          <PinnedRow
            key={item.id}
            item={item}
            isActive={activePinned === item.id}
            count={pinnedCounts[item.id] || 0}
            onPress={onPinnedChange}
          />
        ))}

        <View style={[styles.divider, { backgroundColor: colors.border_color_secondary }]} />

        <View style={styles.sectionHeader}>
          <Text size="s" weight="bold" color="secondary" style={styles.sectionHeaderLabel}>
            {categoryLabel}
          </Text>
          {selectedFacets.size > 0 && (
            <Pressable onPress={onFacetsClear} style={Platform.OS === 'web' ? { cursor: 'pointer' } : undefined}>
              <Text size="s" color="tertiary" weight="semibold">{clearLabel}</Text>
            </Pressable>
          )}
        </View>

        {facets.map(cat => (
          <FacetRow
            key={cat.id}
            facetId={cat.id}
            label={cat.label}
            count={cat.count}
            color={cat.color}
            checked={selectedFacets.has(cat.id)}
            onFacetToggle={onFacetToggle}
          />
        ))}

        {facets.length === 0 && (
          <View style={{ padding: 12 }}>
            <Text size="s" color="tertiary">{emptyLabel}</Text>
          </View>
        )}
      </View>
    </View>
  );
};

CatalogRail.propTypes = {
  pinnedItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  })).isRequired,
  pinnedCounts: PropTypes.object.isRequired,
  activePinned: PropTypes.string.isRequired,
  onPinnedChange: PropTypes.func.isRequired,
  facets: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedFacets: PropTypes.instanceOf(Set).isRequired,
  onFacetToggle: PropTypes.func.isRequired,
  onFacetsClear: PropTypes.func.isRequired,
  categoryLabel: PropTypes.string,
  clearLabel: PropTypes.string,
  emptyLabel: PropTypes.string,
};

const styles = StyleSheet.create({
  rail: {
    width: RAIL_WIDTH,
    borderRightWidth: 1,
  },
  pinned: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    height: 32, paddingHorizontal: 10, marginHorizontal: 4,
    borderRadius: 6,
  },
  facet: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    height: 30, paddingHorizontal: 8, marginHorizontal: 4,
    borderRadius: 6,
  },
  checkbox: {
    width: 14, height: 14, borderRadius: 3,
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 1.5,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: 10, marginHorizontal: 12,
  },
  sectionHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 12, paddingBottom: 6,
  },
  sectionHeaderLabel: {
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
});

export default React.memo(CatalogRail);
